import React, { useEffect, useState } from "react";
import "./UserFilter.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux-store/store";
import {
  IUserLocalFilterState,
  UserControlSlices,
} from "../../../slices/UserControlSlice";
import { IKeyValuePair } from "./UserFilterPopup";
import { GetAllUser } from "../../../api/ssoapi/UserControlapi";
import { ESearchFilterActiveTypes } from "../../../constants/CommonInterfaces";
import { USER_FILTER_CONFIG } from "../modal/CloumnFilter/ExtensiveFilterContent";

// Define types for filter configuration
export interface IUserFilterConfig {
  label: string;
  key: string;
  type: "input" | "select";
  options?: IKeyValuePair[];
}

interface UserExtensiveSearchProps {
  onClose: () => void;
}

const UserExtensiveSearch: React.FC<UserExtensiveSearchProps> = ({
  onClose,
}) => {
  // State to track filter values
  const localFilterState = useSelector(
    (state: RootState) => state.userControl.searchFilter.columnSearch
  );
  const userSearchQuery = useSelector(
    (state: RootState) => state.userControl.searchFilter.apiQuery
  );
  const [isEnableSearch, setIsEnableSearch] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleUpdateFilter = (key: string, value: string) => {
    setIsEnableSearch(false);
    dispatch(
      UserControlSlices.UpdateLocalSearchQuery({ field: key, value: value })
    );
  };

  useEffect(() => {
    if (isEnableSearch) {
      // Create a new search query object
      let lSearchQuery = Object.assign({}, userSearchQuery);

      // Update the search query with valid filters
      lSearchQuery.filters = lSearchQuery.filters = lSearchQuery.filters.filter(
        (x) => {
          // Handle different types of values
          if (typeof x.value === "string") {
            // For strings, check if it's not an empty string after trimming
            return x.value.trim() !== "";
          }

          // For other types (numbers, etc.), check if the value is not null or undefined
          return x.value !== null && x.value !== undefined;
        }
      );
      lSearchQuery.isOr = false;
      lSearchQuery.page = 1;

      // Dispatch actions
      dispatch(
        UserControlSlices.UpdatePagination({
          page: 1,
          pageSize: userSearchQuery.pageSize,
          isPageChanged: false,
        })
      );
      dispatch(UserControlSlices.UpdateGlobalSearchFilter({ value: "" }));
      dispatch(
        UserControlSlices.UpdateLastActiveSearch({
          value: ESearchFilterActiveTypes.column,
        })
      );

      // Fetch users with updated query
      dispatch(GetAllUser(lSearchQuery));

      // Close the filter popup
      onClose();
    }
  }, [isEnableSearch]);

  const handleApplyFilter = () => {
    // Apply temp search values to actual search query
    dispatch(UserControlSlices.ApplyColumnSearchFilter());
    setIsEnableSearch(true);
  };

  // Clear all filters
  const onClearFilters = () => {
    setIsEnableSearch(false);
    let lSearchQuery = Object.assign({}, userSearchQuery);
    lSearchQuery.filters = [];
    lSearchQuery.isOr = false;
    lSearchQuery.page = 1;
    dispatch(
      UserControlSlices.resetLocalSearchQuery({
        canClearAll: true,
        fieldClear: "",
      })
    );

    dispatch(GetAllUser(lSearchQuery));

    onClose();
  };

  return (
    <div style={styles.container}>
      <div style={styles.gridContainer}>
        {USER_FILTER_CONFIG.map((config) => (
          <div key={config.key} style={styles.filterRow}>
            <label htmlFor={config.key} style={styles.label}>
              {config.label}
            </label>

            {config.type === "input" ? (
              <input
                id={config.key}
                type="text"
                placeholder={`Enter ${config.label}`}
                value={
                  localFilterState[config.key as keyof IUserLocalFilterState]
                    .value
                }
                onChange={(e) => handleUpdateFilter(config.key, e.target.value)}
                style={styles.input}
              />
            ) : (
              <select
                value={
                  localFilterState[config.key as keyof IUserLocalFilterState]
                    .value
                }
                onChange={(e) => handleUpdateFilter(config.key, e.target.value)}
                style={styles.select}
              >
                <option value="">Select {config.label}</option>
                {config.options?.map((option) => (
                  <option
                    key={typeof option === "object" ? option.value : option}
                    value={typeof option === "object" ? option.value : option}
                  >
                    {typeof option === "object" ? option.label : option}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>

      <div style={styles.buttonContainer}>
        <button onClick={onClearFilters} className="column-filter-clear-btn">
          Clear
        </button>
        <button
          className="popup-create-btn column-filter-search-btn"
          onClick={handleApplyFilter}
        >
          Search
        </button>
      </div>
    </div>
  );
};

// Styles using standard CSS-in-JS approach
const styles = {
  container: {
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "315px 1fr",
    gap: "16px",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  label: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#333",
    marginRight: "16px",
    minWidth: "90px",
    fontFamily: "DiscoverSans-Medium",
  },
  input: {
    width: "190px",
    flexGrow: 1,
    padding: "8px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "12px",
    outline: "none",
    fontFamily: "DiscoverSans-Medium",
  },
  select: {
    width: "190px",
    flexGrow: 1,
    outline: "none",
    padding: "8px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "DiscoverSans-Medium",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "24px",
    gap: "16px",
  },
};

export default UserExtensiveSearch;
